import {
  CreditCard, Truck, RefreshCw 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  ResolutionType, Discrepancy 
} from '@/types/orders';

interface ActionPanelProps {
  readonly discrepancy: Discrepancy;
  readonly selectedAction: ResolutionType | null;
  readonly creditAmount: string;
  readonly isPending: boolean;
  readonly onSelectAction: (action: ResolutionType | null) => void;
  readonly onCreditAmountChange: (amount: string) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

const RESOLUTION_OPTIONS: {
  type: ResolutionType;
  icon: typeof CreditCard;
  label: string;
}[] = [
  {
    type: 'credit_note',
    icon: CreditCard,
    label: 'Issue Credit' 
  },
  {
    type: 'redelivery',
    icon: Truck,
    label: 'Redeliver' 
  },
  {
    type: 'replacement',
    icon: RefreshCw,
    label: 'Replace' 
  },
];

export function ActionPanel({
  discrepancy,
  selectedAction,
  creditAmount,
  isPending,
  onSelectAction,
  onCreditAmountChange,
  onCancel,
  onSubmit,
}: ActionPanelProps) {
  const calculateSuggestedCredit = () => {
    return discrepancy.items.reduce((sum, item) => sum + item.affectedQuantity * 10, 0);
  };

  return (
    <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
      <p className="text-sm font-medium text-slate-700">Propose Resolution</p>
      <div className="flex gap-2">
        {RESOLUTION_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedAction === option.type;
          return (
            <button
              key={option.type}
              type="button"
              onClick={() => onSelectAction(isSelected ? null : option.type)}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-md border text-sm font-medium transition-colors ${
                isSelected
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {option.label}
            </button>
          );
        })}
      </div>

      {selectedAction === 'credit_note' && (
        <div className="flex gap-2 items-center">
          <input
            type="number"
            value={creditAmount}
            onChange={(e) => onCreditAmountChange(e.target.value)}
            placeholder="Credit amount"
            className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCreditAmountChange(calculateSuggestedCredit().toFixed(2))}
            className="text-slate-600"
          >
            Suggest ${calculateSuggestedCredit()}
          </Button>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="flex-1 bg-slate-900 hover:bg-slate-800"
          onClick={onSubmit}
          disabled={!selectedAction || isPending}
        >
          {isPending ? 'Submitting...' : 'Submit Proposal'}
        </Button>
      </div>
    </div>
  );
}
